import * as Popover from "@radix-ui/react-popover";
import { useWindowSize } from "hooks/useWindowSize";
import { COMMENT_TYPE } from "store/apis/notes";
import classNames from "classnames";
import { AddCommentPop } from "components/AddComment/AddComment";
import { AddCommentAnchor } from "components/AddCommentPopoverAnchor/AddCommentPopoverAnchor";
import { AddCommentPopoverRoot } from "components/AddCommentPopoverRoot/AddCommentPopoverRoot";
import { ConditionalLink } from "components/conditionalLink";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Table from "components/DesignSystem/Table/V2/Table";
import { StartBookkeepingDateEmptyScreen } from "components/GeneralLedger/GeneralLedgerEmptyScreen";
import { ArrowRight } from "components/icons/ArrowRight";
import { Redirect } from "components/icons/Redirect";
import { UsersThree } from "components/icons/UsersThree";
import { TransactionSlider } from "components/Transaction/Slider/TransactionSlider";
import { colors } from "constants/colors";
import { AnimatePresence, motion } from "framer-motion";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { ReportsType } from "pages/Books/Reports/Reports";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  createColumnHelper,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "react-table-8.10.7";
import ThreeDots from "static/images/ThreeDots.svg";
import { Account, ComparisonReportData } from "types/Models/report";
import { getCommonPinningStyles } from "utils/getCommonPinningStyles";
import { getPinnedShadowClass } from "utils/getPinnedShadowClass";
import { redirectToCategories } from "utils/redirectToCategories";
import { stopPropagation } from "utils/stopPropagation";
import { ExpandedTransaction } from "./ExpandedTransaction";
import { ReportFilterType } from "./ReportFilter";
import { useViewByReportDataFormat } from "./hook/useViewByReportDataFormat";
import { TableSelectionControls } from "components/GeneralLedger/TableSelectionControls";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { AccountWithMonths } from "./utils/formatViewReportData";
import { OTHER_ITEM, PERIOD } from "constants/revenueMetrics";
import ConditionalToolTip from "components/design/conditionalToolTip";
import {
  COLUMN_IDS,
  COMPARE_PERIOD,
  TOTALS_COLUMN,
  VARIANCE,
  VARIANCE_COLUMN,
} from "constants/reports";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { REPORT_COMMENT_TYPE } from "./ReportsAccordion";
import { VendorBreakdownRow } from "./VendorBreakdownRow";
import { Vendor } from "components/icons/Vendor";
import { Cross } from "components/icons/Cross";

const DESKTOP_PINNING = { left: ["name"], right: ["actions"] };
// On phones pinned columns fill the screen, so everything scrolls together instead.
const NO_PINNING = { left: [], right: [] };

export const ComparisonReport = ({
  reportType,
  reportId,
  filter,
  showTxns = true,
}: {
  reportType: ReportsType;
  reportId?: string;
  filter: ReportFilterType;
  showTxns?: boolean;
}) => {
  const [selectedRows, setSelectedRows] = useState({});
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.FINANCIALS
  );
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [expandedCell, setExpandedCell] = useState<{
    rowId: string;
    colId: string;
  } | null>(null);
  const [vendorModalState, setVendorModalState] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [scrollContainerWidth, setScrollContainerWidth] = useState(0);
  const leafHeaderRowRef = useRef<HTMLTableRowElement>(null);
  const vendorModalOpenRef = useRef(false);
  const openComment = useAppSelector((store) => store.openComment.openComment);
  const { formatViewReportData, originalData, isFetching, isSuccess } =
    useViewByReportDataFormat({
      reportType,
      reportId,
      filter,
    });
  const { isAdmin } = useRoleBasedView();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { report_data = {} } = originalData || {};

  const reportDatabyView = useMemo(
    () => formatViewReportData,
    [isFetching, JSON.stringify(formatViewReportData)]
  );
  const columnHelper = createColumnHelper<AccountWithMonths>();

  const isCompareView = Boolean(filter.COMPARE_BY);
  const reportDataKeys = Object.keys(report_data);
  const periodKeys = isCompareView
    ? reportDataKeys.filter((key) => key !== VARIANCE_COLUMN)
    : reportDataKeys;

  const viewByColumns = isCompareView
    ? []
    : periodKeys.map((name) => {
        const isTotalsColumn = name === TOTALS_COLUMN;
        return columnHelper.accessor("uuid", {
          id: name,
          size: filter.OTHER === OTHER_ITEM.MERCHANT ? 170 : 114,
          cell: ({ row, column }) => {
            const amount = row.original.monthData?.[name]?.summary_amount || 0;

            if (!Boolean(amount)) {
              return (
                <div
                  className={classNames("t-flex t-justify-end t-ml-4", {
                    "t-font-medium": isTotalsColumn,
                  })}
                >
                  -
                </div>
              );
            }

            return (
              <div
                className={classNames("t-flex t-justify-end t-ml-4", {
                  "t-text-purple t-underline":
                    expandedCell?.rowId === row.id &&
                    expandedCell?.colId === column.id,
                  "hover:t-text-purple hover:t-underline hover:t-cursor-pointer":
                    showTxns && Boolean(row.original.identifier),
                  "t-font-medium": isTotalsColumn,
                })}
                onClick={(event) =>
                  showTxns &&
                  handleAmountClick(
                    event,
                    row.id,
                    column.id,
                    amount,
                    row.original.identifier
                  )
                }
              >
                <AmountSuperScript amount={Number(amount)} />
              </div>
            );
          },
          header: () => (
            <div className="t-flex t-justify-end">
              <ConditionalToolTip
                condition={filter.OTHER === OTHER_ITEM.MERCHANT && name}
              >
                <div
                  className={classNames("t-line-clamp-1 t-text-right", {
                    "t-font-semibold": isTotalsColumn,
                  })}
                >
                  {name}
                </div>
              </ConditionalToolTip>
            </div>
          ),
        });
      });

  const currentPeriodKey =
    periodKeys.find(
      (key) =>
        (report_data as ComparisonReportData)[key]?.compare_period ===
        COMPARE_PERIOD.CURRENT
    ) ||
    periodKeys[0] ||
    "";
  const comparisonPeriodKey =
    periodKeys.find(
      (key) =>
        (report_data as ComparisonReportData)[key]?.compare_period ===
        COMPARE_PERIOD.PREVIOUS
    ) ||
    periodKeys[1] ||
    "";

  const showVarianceAmount = filter.VARIANCE?.includes(VARIANCE.AMOUNT);
  const showVariancePercentage = filter.VARIANCE?.includes(VARIANCE.PERCENTAGE);

  const compareColumns = isCompareView
    ? [
        columnHelper.group({
          id: "Current period",
          header: () => (
            <HeadCellContent title="Current period" align="right" />
          ),
          columns: [
            columnHelper.accessor("uuid", {
              id: "current_period",
              size: 200,
              header: () => (
                <HeadCellContent title={currentPeriodKey} align="right" />
              ),
              cell: ({ row }) => {
                const amount =
                  row.original.monthData?.[currentPeriodKey]?.summary_amount;

                if (amount === null)
                  return <div className="t-flex t-justify-end t-ml-4">-</div>;

                const isExpanded =
                  expandedCell?.rowId === row.id &&
                  expandedCell?.colId === currentPeriodKey;

                return (
                  <div
                    className={classNames("t-flex t-justify-end t-ml-4", {
                      "t-text-purple t-underline": isExpanded,
                      "hover:t-text-purple hover:t-underline hover:t-cursor-pointer":
                        showTxns && Boolean(row.original.identifier),
                    })}
                    onClick={(event) =>
                      showTxns &&
                      handleAmountClick(
                        event,
                        row.id,
                        currentPeriodKey,
                        amount,
                        row.original.identifier
                      )
                    }
                  >
                    <AmountSuperScript amount={Number(amount)} />
                  </div>
                );
              },
            }),
          ],
        }),

        columnHelper.group({
          id: "Comparison period",
          header: () => (
            <HeadCellContent title="Comparison period" align="right" />
          ),
          columns: [
            columnHelper.accessor("uuid", {
              id: "comparison_period",
              size: 200,
              header: () => (
                <HeadCellContent title={comparisonPeriodKey} align="right" />
              ),
              cell: ({ row }) => {
                const amount =
                  row.original.monthData?.[comparisonPeriodKey]?.summary_amount;

                if (amount === null)
                  return <div className="t-flex t-justify-end t-ml-4">-</div>;

                const isExpanded =
                  expandedCell?.rowId === row.id &&
                  expandedCell?.colId === comparisonPeriodKey;

                return (
                  <div
                    className={classNames("t-flex t-justify-end t-ml-4", {
                      "t-text-purple t-underline": isExpanded,
                      "hover:t-text-purple hover:t-underline hover:t-cursor-pointer":
                        showTxns && Boolean(row.original.identifier),
                    })}
                    onClick={(event) =>
                      showTxns &&
                      handleAmountClick(
                        event,
                        row.id,
                        comparisonPeriodKey,
                        amount,
                        row.original.identifier
                      )
                    }
                  >
                    <AmountSuperScript amount={Number(amount)} />
                  </div>
                );
              },
            }),
          ],
        }),

        ...(showVarianceAmount || showVariancePercentage
          ? [
              columnHelper.group({
                id: "Variance",
                header: () => (
                  <HeadCellContent
                    title="Variance"
                    align={
                      showVarianceAmount && showVariancePercentage
                        ? "center"
                        : "right"
                    }
                  />
                ),
                columns: [
                  ...(showVarianceAmount
                    ? [
                        columnHelper.display({
                          id: "variance_amount",
                          header: () => (
                            <HeadCellContent title="Amount ($)" align="right" />
                          ),
                          size: 130,
                          maxSize: 130,
                          cell: ({ row }) => {
                            const variance_amount =
                              row.original.monthData?.Variance
                                ?.summary_variance_amount;

                            if (variance_amount === null)
                              return (
                                <div className="t-flex t-justify-end">-</div>
                              );

                            return (
                              <div className="t-flex t-justify-end">
                                <AmountSuperScript
                                  amount={Number(variance_amount)}
                                />
                              </div>
                            );
                          },
                        }),
                      ]
                    : []),

                  ...(showVariancePercentage
                    ? [
                        columnHelper.display({
                          id: "variance_percentage",
                          header: () => (
                            <HeadCellContent
                              title="Percentage (%)"
                              align="right"
                            />
                          ),
                          size: 130,
                          maxSize: 130,
                          cell: ({ row }) => {
                            const variance_percentage =
                              row.original.monthData?.Variance
                                ?.summary_variance_percentage;

                            if (variance_percentage === null)
                              return (
                                <div className="t-flex t-justify-end">-</div>
                              );

                            return (
                              <div
                                className={classNames("t-flex t-justify-end")}
                              >
                                {(variance_percentage ?? 0).toFixed(2)}%
                              </div>
                            );
                          },
                        }),
                      ]
                    : []),
                ],
              }),
            ]
          : []),
      ]
    : [];

  const columns = [
    columnHelper.accessor("name", {
      header: "Category",
      size: isCompareView ? 270 : 300,
      cell: ({ row, getValue }) => {
        const name = getValue();

        return (
          <div
            style={{
              paddingLeft: `${row.depth * 1}rem`,
            }}
            className={classNames("t-flex t-items-center")}
          >
            <div
              className={classNames({
                "t-rotate-90 t-transform t-transition t-duration-300 t-ease-in-out":
                  row.getIsExpanded(),
                "t-invisible": !row.getCanExpand(),
              })}
            >
              <ArrowRight stroke="1.5" color="currentColor" />
            </div>
            <div className="t-flex t-justify-end t-h-full t-items-center t-gap-3">
              {name}
              {row.depth > 0 && (
                <span className="group-hover:t-visible t-invisible">
                  <ConditionalLink
                    to={redirectToCategories({
                      uuid: row.original.uuid,
                      groupId,
                      entityId,
                      filter,
                      isAdmin,
                    })}
                  >
                    <Redirect size={16} />
                  </ConditionalLink>
                </span>
              )}
            </div>
          </div>
        );
      },
    }),
    ...viewByColumns,
    ...compareColumns,
    columnHelper.display({
      header: "",
      id: "actions",
      cell: ({ row }) => {
        const isVendorBreakdownOpen =
          vendorModalState?.id === row.original.uuid;
        const showVendorBreakdown =
          filter.VIEW_BY && filter.VIEW_BY !== PERIOD.TOTALS;

        return (
          <div className="t-flex t-items-center t-gap-1">
            {showVendorBreakdown && (
              <ConditionalToolTip
                condition={
                  isVendorBreakdownOpen
                    ? "Close vendor breakdown"
                    : "View category vendors"
                }
                side="left"
              >
                <span>
                  <Button
                    customType="ghost_icon"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setVendorModalState((prev) =>
                        prev?.id === row.original.uuid
                          ? null
                          : { id: row.original.uuid, name: row.original.name }
                      );
                      setExpandedCell(null);
                    }}
                    active={isVendorBreakdownOpen}
                  >
                    {isVendorBreakdownOpen ? <Cross /> : <Vendor />}
                  </Button>
                </span>
              </ConditionalToolTip>
            )}
            <AddCommentPopoverRoot contentId={row.original.uuid}>
              <Dropdown.Root modal={false}>
                <Dropdown.Trigger
                  asChild
                  onClick={stopPropagation}
                  disabled={!row.original.identifier}
                >
                  <div>
                    <ConditionalToolTip
                      condition={
                        !row.original.identifier &&
                        "Action is disabled for non COA item"
                      }
                      side="left"
                    >
                      <span>
                        <AddCommentAnchor contentId={row.original.uuid}>
                          <Button
                            disabled={!row.original.identifier}
                            customType="ghost_icon"
                            size="small"
                          >
                            <img src={ThreeDots} alt="Action" />
                          </Button>
                        </AddCommentAnchor>
                      </span>
                    </ConditionalToolTip>
                  </div>
                </Dropdown.Trigger>
                <Dropdown.Portal>
                  <Dropdown.Content onClick={stopPropagation} align="end">
                    <Popover.Trigger asChild>
                      <Dropdown.Item>Add comment</Dropdown.Item>
                    </Popover.Trigger>
                  </Dropdown.Content>
                </Dropdown.Portal>
                <Popover.Portal>
                  <AddCommentPop
                    commentType={REPORT_COMMENT_TYPE[reportType]}
                    contentId={row.original.uuid}
                  />
                </Popover.Portal>
              </Dropdown.Root>
            </AddCommentPopoverRoot>
          </div>
        );
      },
      minSize: 90,
      size: 90,
    }),
  ];

  useEffect(() => {
    const getParentTree = (
      ledger: typeof reportDatabyView,
      selectedCategoryId: string | null,
      currentParents: string[] = []
    ): string[] | undefined => {
      for (const category of ledger) {
        if (category.uuid === selectedCategoryId) {
          return currentParents;
        }

        if (category.types) {
          const parent = getParentTree(category.types, selectedCategoryId, [
            category.uuid,
            ...currentParents,
          ]);
          if (parent?.length) {
            return parent;
          }
        }
      }
    };

    if (openComment) {
      const parents = getParentTree(reportDatabyView, openComment);
      if (parents && parents.length > 0) {
        setExpanded((expanded) =>
          typeof expanded === "object"
            ? parents.reduce(
                (acc, id) => ({ ...acc, [id]: true }),
                expanded || {}
              )
            : expanded
        );
      }
    }
  }, [openComment, reportDatabyView]);

  const { isMobile } = useWindowSize();

  const table = useReactTable({
    data: reportDatabyView,
    columns: columns,
    state: {
      expanded,
      columnPinning: isMobile ? NO_PINNING : DESKTOP_PINNING,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.types,
    getRowId: (row) => row.uuid,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  const showBorderForSticky = useCallback((columnId: string) => {
    return ["name", "actions"].includes(columnId);
  }, []);

  const handleAmountClick = (
    event: React.MouseEvent,
    rowId: string,
    colId: string,
    amount: number | undefined,
    identifier: number | null
  ) => {
    if (!amount || !identifier) return;

    event.stopPropagation();
    setExpandedCell((prev) => {
      const coord = { rowId, colId };

      if (prev?.rowId === coord.rowId && prev?.colId === coord.colId) {
        return null;
      } else {
        return coord;
      }
    });
  };

  vendorModalOpenRef.current = vendorModalState !== null;

  useEffect(() => {
    const measure = () => {
      if (vendorModalOpenRef.current) return;

      const row = leafHeaderRowRef.current;
      if (!row) return;

      const ths = row.querySelectorAll("th");
      const leafColumns = table.getAllLeafColumns();
      const widths: Record<string, number> = {};
      leafColumns.forEach((col, i) => {
        if (ths[i]) widths[col.id] = ths[i].offsetWidth;
      });
      setColumnWidths(widths);

      const scrollContainer = row.closest(".t-overflow-auto");
      if (scrollContainer) setScrollContainerWidth(scrollContainer.clientWidth);
    };

    measure();

    const observer = new ResizeObserver(measure);
    if (leafHeaderRowRef.current) observer.observe(leafHeaderRowRef.current);

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDatabyView]);

  const firstVarianceColumnId = (() => {
    if (showVarianceAmount) return COLUMN_IDS.VARIANCE_AMOUNT;
    if (showVariancePercentage) return COLUMN_IDS.VARIANCE_PERCENTAGE;
    return null;
  })();

  const isCompareBodyBorderColumn = (columnId: string) => {
    if (!isCompareView) return false;

    return (
      columnId === COLUMN_IDS.COMPARISON_PERIOD ||
      columnId === firstVarianceColumnId
    );
  };

  const showHeaderVerticalBorder = (columnId: string) =>
    (
      [COLUMN_IDS.CURRENT_PERIOD, COLUMN_IDS.COMPARISON_PERIOD] as string[]
    ).includes(columnId);

  return (
    <Async.Root {...{ isEmpty: false, isLoading: isFetching, isSuccess }}>
      <Async.Empty>
        <StartBookkeepingDateEmptyScreen />
      </Async.Empty>
      <Async.Success>
        <div className="t-relative t-flex t-h-full t-flex-col t-pb-20">
          <Table.Container
            sticky
            className="t-w-full t-grow t-overflow-auto t-pb-24 scrollbar-visible t-min-h-0"
          >
            <Table.Content className="t-w-full">
              <Table.Head>
                {table.getHeaderGroups().map((headerGroup, groupIndex) => {
                  const isGroupHeader = headerGroup.depth === 0;
                  const isLeafRow =
                    groupIndex === table.getHeaderGroups().length - 1;

                  return (
                    <Table.HeadRow
                      key={headerGroup.id}
                      ref={isLeafRow ? leafHeaderRowRef : undefined}
                    >
                      {headerGroup.headers.map((header, index) => {
                        const styles = getCommonPinningStyles(header.column);
                        const { boxShadow } = getPinnedShadowClass(
                          header.column
                        );
                        const isPinnedColumn = header.column.getIsPinned();
                        return (
                          <Table.HeadCell
                            isUpperCase={false}
                            key={header.id}
                            style={{
                              ...styles,
                              minWidth: header.column.getSize(),
                            }}
                            colSpan={header.colSpan}
                            className={classNames(
                              "!t-text-subtext-sm",
                              boxShadow,
                              {
                                "!t-bg-surface-lighter-grey": isPinnedColumn,

                                "t-drop-shadow-border-shadow-neutral-0":
                                  showBorderForSticky(header.column.id),

                                "!t-z-[3]":
                                  showBorderForSticky(header.column.id) &&
                                  header.column.getIsPinned(),
                                "!t-text-surface-lighter-grey":
                                  isGroupHeader && header.column.id === "name",
                                "t-border-r t-border-neutral-0 ":
                                  isCompareView &&
                                  (showHeaderVerticalBorder(header.column.id) ||
                                    (!showHeaderVerticalBorder(
                                      header.column.id
                                    ) &&
                                      header.colSpan > 0)),
                              }
                            )}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </Table.HeadCell>
                        );
                      })}
                    </Table.HeadRow>
                  );
                })}
              </Table.Head>
              <Table.Body>
                {table.getRowModel().rows.map((row) => {
                  const isRowCellExpanded = expandedCell?.rowId === row.id;

                  return (
                    <Fragment key={row.id}>
                      <motion.tr
                        key={row.id}
                        aria-label={row.original.name}
                        aria-disabled={!row.getCanExpand()}
                        aria-expanded={row.getIsExpanded()}
                        onClick={() => {
                          row.toggleExpanded();
                        }}
                        className={classNames(
                          "t-select-none t-px-3 t-text-body-sm t-group t-border t-border-b t-border-solid t-border-t-0 t-border-x-0 t-border-neutral-0",
                          {
                            "t-text-text-100": row.depth === 0,
                            "t-text-text-60": row.depth === 1,
                            "t-text-text-30": row.depth >= 2,
                            "t-cursor-pointer hover:t-bg-surface-lighter-grey":
                              row.getCanExpand(),
                            "animate-transaction-row":
                              openComment === row.original.uuid,
                          }
                        )}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                      >
                        {row.getVisibleCells().map((cell) => {
                          const styles = getCommonPinningStyles(cell.column);
                          const { boxShadow } = getPinnedShadowClass(
                            cell.column
                          );

                          return (
                            <Table.Cell
                              key={cell.id}
                              style={{
                                ...styles,
                                minWidth: cell.column.getSize(),
                                ...(cell.column.id === "name"
                                  ? { width: "100px", minWidth: "100px" }
                                  : {}),
                                ...(cell.column.id === "actions"
                                  ? { width: "30px", minWidth: "30px" }
                                  : {}),
                                ...(expandedCell?.rowId === row.id &&
                                expandedCell?.colId === cell.column.id
                                  ? {
                                      background:
                                        colors.surface["lighter-grey"],
                                    }
                                  : {}),
                              }}
                              className={classNames(boxShadow, {
                                "t-text-purple":
                                  expandedCell?.rowId === row.id &&
                                  expandedCell?.colId === cell.column.id,

                                "t-drop-shadow-border-shadow-neutral-0":
                                  showBorderForSticky(cell.column.id),

                                "!t-z-[1]":
                                  showBorderForSticky(cell.column.id) &&
                                  !cell.column.getIsPinned(),

                                "!t-z-[3]":
                                  showBorderForSticky(cell.column.id) &&
                                  cell.column.getIsPinned(),

                                "t-border-l t-border-neutral-0":
                                  isCompareBodyBorderColumn(cell.column.id),
                              })}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </Table.Cell>
                          );
                        })}
                      </motion.tr>
                      <AnimatePresence mode="wait">
                        {isRowCellExpanded && (
                          <ExpandedTransaction
                            setExpandedCell={setExpandedCell}
                            expandedCell={expandedCell}
                            filter={filter}
                            reportData={report_data as ComparisonReportData}
                            row={row}
                            columns={columns}
                            columnWidths={columnWidths}
                            scrollContainerWidth={scrollContainerWidth}
                            reportType={reportType}
                            selectedRows={selectedRows}
                            setSelectedRows={setSelectedRows}
                          />
                        )}
                      </AnimatePresence>
                      <AnimatePresence mode="wait">
                        {vendorModalState?.id === row.original.uuid && (
                          <VendorBreakdownRow
                            categoryId={row.original.uuid}
                            categoryDepth={row.depth}
                            filter={filter}
                            reportType={reportType}
                            displayPeriodKeys={
                              isCompareView
                                ? [currentPeriodKey, comparisonPeriodKey]
                                : periodKeys
                            }
                            parentColumns={table.getVisibleLeafColumns() as any}
                            columnWidths={columnWidths}
                            scrollContainerWidth={scrollContainerWidth}
                            parentColumnCount={
                              table.getVisibleLeafColumns().length
                            }
                            onClose={() => setVendorModalState(null)}
                            selectedRows={selectedRows}
                            setSelectedRows={setSelectedRows}
                          />
                        )}
                      </AnimatePresence>
                    </Fragment>
                  );
                })}
              </Table.Body>
            </Table.Content>
          </Table.Container>
          <TransactionSlider />
          <TableSelectionControls
            deselectRows={() => {
              setSelectedRows?.({});
            }}
            allSelectedRows={selectedRows || {}}
            reportType={reportType}
            hasWritePermission={hasWritePermission}
          />
        </div>
      </Async.Success>
    </Async.Root>
  );
};
